import { useRef, useState } from 'react'

import {
  ActionIcon,
  Alert,
  CloseButton,
  Divider,
  Textarea,
  Tooltip,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import EmojiPicker from 'src/components/Inputs/EmojiPicker/EmojiPicker'
import { ActivityLogType } from 'src/graphql/types/activityLogs'
import useUploadDocument from 'src/hooks/useUploadDocument/useUploadDocument'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import IconPlane from 'src/icons/IconPlane'
import {
  SMS_HARD_LIMIT,
  SMS_HARD_LIMIT_MESSAGE,
  SMS_SPAM_WARNING,
  SMS_WARNING_THRESHOLD,
} from 'src/lib/sms'
import { focusCaretAfterInsert, insertAtCaret } from 'src/lib/textInput.utils'

import SMSInputMenu from './components/SMSInputMenu'

const SMSInput = ({ sendSMS, applicantId }) => {
  const [selectedFiles, setSelectedFiles] = useState([])
  const textTemplatesRef = useRef(null)
  const contentRef = useRef<HTMLTextAreaElement>(null)
  const { handleCreateDocuments } = useUploadDocument(
    ['GET_DOCUMENTS'],
    applicantId
  )

  const SMSInputSchema = z.object({
    content: z
      .string()
      .min(1, { message: 'Enter at least 1 character.' })
      .max(SMS_HARD_LIMIT, SMS_HARD_LIMIT_MESSAGE),
  })

  const form = useForm({
    initialValues: {
      content: '',
    },
    validateInputOnChange: true,
    validate: zodResolver(SMSInputSchema),
  })

  const contentLength = (form.values.content || '').length
  const showSpamWarning = contentLength > SMS_WARNING_THRESHOLD

  const handleEmojiSelect = (emoji: string) => {
    const { content, caret } = insertAtCaret(
      contentRef.current,
      // useForm infers this field as unknown here, matching how contentLength
      // above guards it.
      String(form.values.content ?? ''),
      emoji
    )
    form.setFieldValue('content', content)
    focusCaretAfterInsert(contentRef.current, caret)
  }

  const onFormSubmit = (values) => {
    const onDocumentsCreated = (documents) => {
      sendSMS({
        type: ActivityLogType.SMS,
        documents: [...documents],
        ...values,
      })
    }

    if (selectedFiles.length > 0) {
      handleCreateDocuments(selectedFiles, onDocumentsCreated)
    } else {
      sendSMS({
        type: ActivityLogType.SMS,
        ...values,
      })
    }
    form.reset()
    textTemplatesRef?.current?.resetTemplate()
    setSelectedFiles([])
  }

  return (
    <div>
      <form onSubmit={form.onSubmit((values) => onFormSubmit(values))}>
        <div className="flex flex-col gap-1 rounded-lg border px-2">
          <Textarea
            ref={contentRef}
            variant="unstyled"
            placeholder="Type message..."
            autosize={true}
            rightSectionWidth={100}
            rightSectionPointerEvents="all"
            rightSection={
              <div className="flex flex-row items-center gap-1">
                <SMSInputMenu
                  applicantId={applicantId}
                  selectedFiles={selectedFiles}
                  setSelectedFiles={setSelectedFiles}
                  onTemplateSelect={(content) =>
                    form.setFieldValue('content', content)
                  }
                />
                <EmojiPicker onSelect={handleEmojiSelect} />
                <Tooltip
                  label="Send message"
                  position="top"
                  className="text-xs"
                >
                  <ActionIcon
                    type="submit"
                    disabled={!form.isValid()}
                    variant="transparent"
                    size={28}
                    radius="md"
                    aria-label="Send message"
                    className="bg-doubleNickel-brand-500 transition-colors hover:bg-doubleNickel-brand-700 disabled:bg-doubleNickel-gray-100"
                  >
                    <IconPlane
                      className={`h-4 w-4 fill-none ${
                        form.isValid()
                          ? 'stroke-doubleNickel-white'
                          : 'stroke-doubleNickel-gray-400'
                      }`}
                    />
                  </ActionIcon>
                </Tooltip>
              </div>
            }
            maxRows={4}
            className="flex-1"
            withAsterisk
            {...form.getInputProps('content')}
          />
          {selectedFiles.length > 0 && (
            <>
              <Divider />
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex flex-row items-center justify-between rounded-sm text-xs font-medium text-doubleNickel-gray-900 hover:bg-doubleNickel-brand-25"
                >
                  {file.name}
                  <CloseButton
                    size="xs"
                    onClick={() => {
                      setSelectedFiles(
                        selectedFiles.filter(
                          (selectedFile) => selectedFile.name !== file.name
                        )
                      )
                    }}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      </form>
      {showSpamWarning && (
        <Alert
          icon={
            <IconAlertTriangle className="h-4 w-4 fill-none stroke-doubleNickel-warning-500" />
          }
          color="yellow"
          variant="light"
          classNames={{
            root: 'mt-2 py-2',
            body: 'gap-0',
            message: 'text-xs',
          }}
        >
          {SMS_SPAM_WARNING}
        </Alert>
      )}
    </div>
  )
}

export default SMSInput
